import Button from 'antd/es/button';
import Form from 'antd/es/form';
import InputNumber from 'antd/es/input-number';
import Slider from 'antd/es/slider';
import React from 'react';
import { styled } from 'styled-components';

interface QuickValue {
  value: number;
  label: string;
}

interface SliderMark {
  value: number;
  label: string;
}

interface EnhancedNumberInputProps {
  label: string;
  name: string;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  required?: boolean;
  help?: string;
  useSlider?: boolean;
  sliderMarks?: SliderMark[];
  quickValues?: QuickValue[];
  showValueHint?: boolean;
}

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.marginXS}px;
`;

const SliderContainer = styled.div`
  margin: ${({ theme }) => theme.marginXS}px 0;
  padding: 0 12px;

  .ant-slider-mark-text {
    white-space: nowrap;

    &:first-child {
      left: 0 !important;
      transform: translateX(0%) !important;
    }

    &:last-child {
      right: 0 !important;
      left: auto !important;
      transform: translateX(0%) !important;
    }

    &:not(:first-child):not(:last-child) {
      transform: translateX(-50%) !important;
    }
  }
`;

const QuickValuesContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.marginXS}px;
  flex-wrap: wrap;
`;

const QuickValueButton = styled(Button)`
  font-size: ${({ theme }) => theme.fontSizeSM}px;
  height: auto;
  padding: ${({ theme }) => `${theme.paddingXXS}px ${theme.paddingXS}px`};
`;

const ValueHint = styled.div`
  font-size: ${({ theme }) => theme.fontSizeSM}px;
  color: ${({ theme }) => theme.colorTextSecondary};
  font-style: italic;
`;

export const EnhancedNumberInput: React.FC<EnhancedNumberInputProps> = ({
  label,
  name,
  min = 0,
  max = 100,
  step = 1,
  placeholder,
  required = false,
  help,
  useSlider = false,
  sliderMarks,
  quickValues,
  showValueHint = false,
}) => {
  const form = Form.useFormInstance();
  const currentValue = Form.useWatch(name, form);

  const handleQuickValueClick = (value: number) => {
    form.setFieldValue(name, value);
  };

  const handleSliderChange = (value: number) => {
    // Ensure we store a proper number
    form.setFieldValue(name, Number(value));
  };

  const getValueHint = (value: number): string => {
    if (sliderMarks) {
      const closestMark = sliderMarks.reduce((prev, curr) =>
        Math.abs(curr.value - value) < Math.abs(prev.value - value)
          ? curr
          : prev,
      );
      return closestMark.label;
    }
    return '';
  };

  const formattedMarks = sliderMarks
    ? sliderMarks.reduce(
        (acc, mark) => {
          acc[mark.value] = mark.label;
          return acc;
        },
        {} as Record<number, string>,
      )
    : undefined;

  return (
    <Form.Item
      label={label}
      name={name}
      rules={
        required
          ? [{ required: true, message: `${label} is required` }]
          : undefined
      }
      help={help}
      normalize={(value) =>
        value === null || value === undefined || value === ''
          ? undefined
          : Number(value)
      }
    >
      <InputContainer>
        <InputNumber
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          style={{ width: '100%' }}
        />

        {useSlider && (
          <SliderContainer>
            <Slider
              min={min}
              max={max}
              step={step}
              marks={formattedMarks}
              value={currentValue}
              onChange={handleSliderChange}
            />
          </SliderContainer>
        )}

        {showValueHint &&
          currentValue !== undefined &&
          getValueHint(currentValue) && (
            <ValueHint>{getValueHint(currentValue)}</ValueHint>
          )}

        {quickValues && quickValues.length > 0 && (
          <QuickValuesContainer>
            {quickValues.map((quickValue) => (
              <QuickValueButton
                key={quickValue.value}
                size="small"
                type={currentValue === quickValue.value ? 'primary' : 'default'}
                onClick={() => handleQuickValueClick(quickValue.value)}
              >
                {quickValue.label}
              </QuickValueButton>
            ))}
          </QuickValuesContainer>
        )}
      </InputContainer>
    </Form.Item>
  );
};
